import { Button } from '../../../../components/ui/button';
import { DropDown } from '../../../../components/ui/dropDown';
import { Panel } from '../../../../components/ui/panel';
import { TextField } from '../../../../components/ui/textField';

import styles from './GamesFilterToolbar.module.css';
import type { GamesFilterToolbarProps } from './types';
import { FilterState } from '../../types';

function getFilterSummary(filters: FilterState, visibleCount: number): string {
    const active = [
        filters.search.trim() && 'search',
        filters.genre && 'genre',
        filters.mechanic && 'mechanic',
        filters.volatility && 'volatility',
    ].filter(Boolean);

    if (active.length === 0) {
        return '';
    }

    return `${visibleCount} game${visibleCount === 1 ? '' : 's'} shown`;
}

export function GamesFilterToolbar({
    filters,
    filterOptions,
    visibleCount,
    onFiltersChange,
    onClearFilters,
}: GamesFilterToolbarProps) {
    const filterSummary = getFilterSummary(filters, visibleCount);
    const hasActiveFilters = Boolean(filterSummary);

    const updateFilters = (patch: Partial<FilterState>) => {
        onFiltersChange({ ...filters, ...patch });
    };

    return (
        <Panel className={styles.toolbar}>
            <h2 className={styles.title}>Browse games</h2>
            <div className={styles.row}>
                <TextField
                    id="games-search"
                    placeholder="Search by name or description…"
                    autoComplete="off"
                    aria-label="Search games"
                    value={filters.search}
                    onChange={(event) => updateFilters({ search: event.target.value })}
                />
                <div className={styles.filters}>
                    <label htmlFor="filter-genre" className={styles.visuallyHidden}>
                        Filter by genre
                    </label>
                    <DropDown
                        id="filter-genre"
                        aria-label="Filter by genre"
                        value={filters.genre}
                        onChange={(event) => updateFilters({ genre: event.target.value })}
                    >
                        <option value="">All genres</option>
                        {filterOptions.genres.map((genre: any) => (
                            <option key={genre} value={genre}>
                                {genre}
                            </option>
                        ))}
                    </DropDown>

                    <label htmlFor="filter-mechanic" className={styles.visuallyHidden}>
                        Filter by mechanic
                    </label>
                    <DropDown
                        id="filter-mechanic"
                        aria-label="Filter by mechanic"
                        value={filters.mechanic}
                        onChange={(event) => updateFilters({ mechanic: event.target.value })}
                    >
                        <option value="">All mechanics</option>
                        {filterOptions.mechanics.map((mechanic: any) => (
                            <option key={mechanic} value={mechanic}>
                                {mechanic}
                            </option>
                        ))}
                    </DropDown>

                    <label htmlFor="filter-volatility" className={styles.visuallyHidden}>
                        Filter by volatility
                    </label>
                    <DropDown
                        id="filter-volatility"
                        aria-label="Filter by volatility"
                        value={filters.volatility}
                        onChange={(event) => updateFilters({ volatility: event.target.value })}
                    >
                        <option value="">Any volatility</option>
                        <option value="5">5 stars</option>
                        <option value="4">4 stars</option>
                        <option value="3">3 stars</option>
                        <option value="2">2 stars</option>
                        <option value="1">1 star</option>
                    </DropDown>

                    <Button
                        variant="secondary"
                        size="md"
                        aria-label="Clear all filters"
                        onClick={onClearFilters}
                    >
                        Clear filters
                    </Button>
                </div>
            </div>
            {hasActiveFilters ? (
                <p className={styles.summary} aria-live="polite">
                    {filterSummary}
                </p>
            ) : null}
        </Panel>
    );
}
